// 고른 객체의 속성 패널 — 오너 시안(2026-09-22, 한글의 '표 속성' 도구)을 따른다.
//
//   위에 행 삽입 · 아래 행 삽입 · 왼쪽 열 삽입 · 오른쪽에 열을 삽입
//   셀 병합 · 셀 분할 · 행 삭제 · 열 삭제
//   테두리(두께·모양·색) · 음영 · 글자 크기 — **고른 칸들만** 또는 표 전체
//   위치/크기(px) · 정렬(왼쪽/가운데/오른쪽) · 텍스트 정렬(좌/중앙/우)
//
// 표 칸이 곧 입력 칸이 될 수 있다 — "필드 팔레트에서 설정할 수 있던 부분들이
// 엑셀 블럭에서 선택이 가능해야" 한다는 요청대로 칸 종류(팔레트 7종)·자동 채움·
// 채울 사람·필수를 여기서 정한다. 칸에 사진도 넣는다(품목 사진).

import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import {
  LAYOUT_CELL_FIELD_TYPES,
  LAYOUT_PAGE_W,
  DEFAULT_CELL_IMAGE_PADDING,
  DEFAULT_LAYOUT_TEXT_ALIGN,
  MIN_LAYOUT_CELL,
  layoutApplyBorders,
  layoutDeleteCol,
  layoutDeleteRow,
  layoutExpandRange,
  layoutInsertCol,
  layoutInsertRow,
  layoutLineWithEnds,
  layoutMasterOf,
  layoutMergeCells,
  layoutResizeTable,
  layoutSetColWidth,
  layoutSetRowHeight,
  layoutSplitCell,
  layoutTablePen,
  type FieldType,
  type LayoutBorder,
  type LayoutBorderApply,
  type LayoutCellField,
  type LayoutImageFit,
  type LayoutImageRef,
  type LayoutObject,
  type LayoutTable,
  type LayoutTableCell,
  type LayoutTextAlign,
  type SignerRole,
} from '@bhops/shared';
import type { CellRange } from './useLayoutEditor';
import { mastersInRange } from './useLayoutEditor';

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: '텍스트',
  signature: '서명',
  stamp: '도장',
  date: '날짜',
  checkbox: '체크박스',
  dropdown: '드롭다운',
  image: '이미지',
};

const BINDING_OPTIONS: { value: string; label: string; binding: LayoutCellField['binding'] }[] = [
  { value: 'literal', label: '직접 입력', binding: { kind: 'literal' } },
  {
    value: 'customer.businessName',
    label: '고객사 상호명',
    binding: { kind: 'customer', path: 'businessName' },
  },
  {
    value: 'customer.businessNumber',
    label: '고객사 사업자번호',
    binding: { kind: 'customer', path: 'businessNumber' },
  },
  {
    value: 'customer.ceoName',
    label: '고객사 대표자',
    binding: { kind: 'customer', path: 'ceoName' },
  },
  {
    value: 'customer.address',
    label: '고객사 주소',
    binding: { kind: 'customer', path: 'address' },
  },
  {
    value: 'robot.serialNumber',
    label: '로봇 시리얼',
    binding: { kind: 'robot', path: 'serialNumber' },
  },
  { value: 'robot.modelName', label: '로봇 모델', binding: { kind: 'robot', path: 'modelName' } },
];

function bindingKey(b: LayoutCellField['binding']): string {
  if (!b || b.kind === 'literal') return 'literal';
  if (b.kind === 'customer' || b.kind === 'robot') return `${b.kind}.${b.path}`;
  return 'literal';
}

// ── 작은 부품 ─────────────────────────────────────────────────────────────

// 구획 제목 — 회색 띠 + 왼쪽 청록 막대. 예전엔 작은 회색 글자뿐이라 '글자'·'테두리'·'음영'이
// 한 덩어리로 보였다(2026-09-23 오너 지적 "구분감이 너무 없다").
const sectionTitle: CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: 'var(--fg-default)',
  background: 'var(--bg-muted)',
  borderLeft: '3px solid var(--fg-brand)',
  borderRadius: 4,
  padding: '6px 9px',
  margin: '18px 0 10px',
};

const selectSt: CSSProperties = {
  width: '100%',
  padding: '5px 7px',
  fontSize: 13,
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-input)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
};

const inputSt: CSSProperties = { ...selectSt };
const smallBtn: CSSProperties = {
  ...selectSt,
  width: 'auto',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

/** 칸 사진 버튼 — 흰 작은 버튼이라 안 보였다(2026-09-23 오너 지적). 서식 관리와 같은 모양. */
const photoBtn: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  flex: '1 1 auto',
  padding: '8px 10px',
  borderRadius: 'var(--r-btn)',
  border: '1.5px solid var(--fg-brand)',
  background: 'color-mix(in srgb, var(--fg-brand) 8%, var(--bg-surface))',
  color: 'var(--fg-brand)',
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 700,
};

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        fontSize: 12,
        flex: 1,
        minWidth: 0,
      }}
    >
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      {children}
    </label>
  );
}

// ── 표 도구 아이콘 (한글 도구 상자 느낌의 작은 SVG) ────────────────────────
// 행 삭제 = 가로로 길쭉한 직사각형에 X, 열 삭제 = 세로로 길쭉한 직사각형에 X (오너 지정).

const ICON = 22;
function Grid({ children }: { children: ReactNode }) {
  return (
    <svg
      width={ICON}
      height={ICON}
      viewBox="0 0 22 22"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      aria-hidden
    >
      {children}
    </svg>
  );
}
const icons = {
  rowAbove: (
    <Grid>
      <rect x="2" y="8" width="18" height="12" />
      <line x1="2" y1="14" x2="20" y2="14" />
      <line x1="11" y1="8" x2="11" y2="20" />
      <path d="M11 1v5M8.5 3.5 11 1l2.5 2.5" strokeWidth="1.6" />
    </Grid>
  ),
  rowBelow: (
    <Grid>
      <rect x="2" y="2" width="18" height="12" />
      <line x1="2" y1="8" x2="20" y2="8" />
      <line x1="11" y1="2" x2="11" y2="14" />
      <path d="M11 21v-5M8.5 18.5 11 21l2.5-2.5" strokeWidth="1.6" />
    </Grid>
  ),
  colLeft: (
    <Grid>
      <rect x="8" y="2" width="12" height="18" />
      <line x1="14" y1="2" x2="14" y2="20" />
      <line x1="8" y1="11" x2="20" y2="11" />
      <path d="M1 11h5M3.5 8.5 1 11l2.5 2.5" strokeWidth="1.6" />
    </Grid>
  ),
  colRight: (
    <Grid>
      <rect x="2" y="2" width="12" height="18" />
      <line x1="8" y1="2" x2="8" y2="20" />
      <line x1="2" y1="11" x2="14" y2="11" />
      <path d="M21 11h-5M18.5 8.5 21 11l-2.5 2.5" strokeWidth="1.6" />
    </Grid>
  ),
  merge: (
    <Grid>
      <rect x="2" y="3" width="18" height="16" />
      <line x1="11" y1="3" x2="11" y2="8" strokeDasharray="2 1.5" />
      <line x1="11" y1="14" x2="11" y2="19" strokeDasharray="2 1.5" />
      <path d="M6 11h4M8 9l2 2-2 2M16 11h-4M14 9l-2 2 2 2" strokeWidth="1.5" />
    </Grid>
  ),
  split: (
    <Grid>
      <rect x="2" y="3" width="18" height="16" />
      <line x1="11" y1="3" x2="11" y2="19" />
      <path d="M9 11H4M6 9l-2 2 2 2M13 11h5M16 9l2 2-2 2" strokeWidth="1.5" />
    </Grid>
  ),
  // 가로로 길쭉한 직사각형 + X
  rowDelete: (
    <Grid>
      <rect x="2" y="7" width="18" height="8" rx="1" />
      <path d="M8 9l6 4M14 9l-6 4" strokeWidth="1.7" stroke="var(--fg-danger)" />
    </Grid>
  ),
  // 세로로 길쭉한 직사각형 + X
  colDelete: (
    <Grid>
      <rect x="7" y="2" width="8" height="18" rx="1" />
      <path d="M9 8l4 6M13 8l-4 6" strokeWidth="1.7" stroke="var(--fg-danger)" />
    </Grid>
  ),
};

function Tool({
  icon,
  label,
  onClick,
  disabled,
  testId,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  testId?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      title={label.replace('\n', ' ')}
      aria-label={label.replace('\n', ' ')}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        width: 60,
        padding: '6px 2px',
        border: '1px solid transparent',
        borderRadius: 'var(--r-btn)',
        background: 'transparent',
        color: disabled ? 'var(--text-secondary)' : 'var(--fg-default)',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        fontSize: 11,
        lineHeight: 1.2,
      }}
    >
      {icon}
      <span style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{label}</span>
    </button>
  );
}

/** 숫자 칸 — 치는 동안은 그대로 두고, 손을 떼면 반영한다. */
function Num({
  label,
  value,
  min,
  max,
  onCommit,
  testId,
}: {
  label: string;
  value: number;
  min?: number;
  max?: number;
  onCommit: (v: number) => void;
  testId?: string;
}) {
  const [raw, setRaw] = useState(String(value));
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setRaw(String(value));
  }, [value, focused]);
  const commit = () => {
    const n = Number(raw);
    if (!Number.isFinite(n)) {
      setRaw(String(value));
      return;
    }
    const clamped = Math.max(min ?? -Infinity, Math.min(max ?? Infinity, Math.round(n)));
    onCommit(clamped);
    setRaw(String(clamped));
  };
  return (
    <Row label={label}>
      <input
        type="number"
        data-testid={testId}
        style={inputSt}
        value={raw}
        min={min}
        max={max}
        onFocus={() => setFocused(true)}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
      />
    </Row>
  );
}

function Seg<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { v: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {options.map((o) => (
        <button
          key={o.v}
          type="button"
          onClick={() => onChange(o.v)}
          style={{
            flex: 1,
            padding: '6px 0',
            fontSize: 13,
            border: '1px solid',
            borderColor: value === o.v ? 'var(--fg-brand)' : 'var(--border-default)',
            borderRadius: 'var(--r-btn)',
            background:
              value === o.v
                ? 'color-mix(in srgb, var(--fg-brand) 12%, transparent)'
                : 'var(--bg-surface)',
            color: 'var(--fg-default)',
            cursor: 'pointer',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

const HEX = /^#?[0-9a-fA-F]{6}$/;

/**
 * 색 고르기 — 고르개와 **색 코드(#RRGGBB)** 둘 다(오너 요청 2026-09-23). 코드는 치는
 * 동안은 그대로 두고, Enter·포커스 이탈에 반영한다. 잘못 치면 원래 값으로 돌아간다.
 */
export function ColorInput({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange: (hex: string) => void;
  testId?: string;
}) {
  const [raw, setRaw] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setRaw(value);
  }, [value, focused]);
  const commit = () => {
    const t = raw.trim();
    if (HEX.test(t)) {
      const v = (t.startsWith('#') ? t : `#${t}`).toLowerCase();
      if (v !== value.toLowerCase()) onChange(v);
      setRaw(v);
    } else {
      setRaw(value);
    }
  };
  return (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <input
        type="color"
        aria-label="색 고르기"
        value={HEX.test(value) ? value : '#000000'}
        onChange={(e) => onChange(e.target.value)}
        style={{ ...inputSt, width: 30, padding: 2, height: 28, flex: '0 0 auto' }}
      />
      <input
        type="text"
        data-testid={testId}
        aria-label="색 코드"
        value={raw}
        maxLength={7}
        spellCheck={false}
        onFocus={() => setFocused(true)}
        onChange={(e) => setRaw(e.target.value)}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        style={{ ...inputSt, fontFamily: 'ui-monospace, monospace', minWidth: 0 }}
      />
    </div>
  );
}

function BorderEditor({
  value,
  onChange,
  testId,
}: {
  value: LayoutBorder;
  onChange: (b: LayoutBorder) => void;
  testId?: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 6 }}>
      <Row label="두께">
        <select
          style={selectSt}
          data-testid={testId}
          value={String(value.width)}
          onChange={(e) => onChange({ ...value, width: Number(e.target.value) })}
        >
          <option value="0">없음</option>
          <option value="0.5">½ pt</option>
          <option value="1">1 pt</option>
          <option value="1.5">1½ pt</option>
          <option value="2">2 pt</option>
          <option value="3">3 pt</option>
          {/* 엑셀에서 온 두께(이중선 2.5 등)가 목록에 없으면 그 값을 그대로 보여 준다 —
              없으면 선택칸이 엉뚱한 값을 보여 준다. */}
          {![0, 0.5, 1, 1.5, 2, 3].includes(value.width) && (
            <option value={String(value.width)}>{value.width} pt</option>
          )}
        </select>
      </Row>
      <Row label="모양">
        <select
          style={selectSt}
          value={value.style}
          onChange={(e) => onChange({ ...value, style: e.target.value as LayoutBorder['style'] })}
        >
          <option value="solid">실선</option>
          <option value="dashed">점선</option>
        </select>
      </Row>
      <Row label="펜 색">
        <ColorInput
          testId={testId ? `${testId.replace(/-width$/, '')}-color` : undefined}
          value={value.color}
          onChange={(v) => onChange({ ...value, color: v })}
        />
      </Row>
    </div>
  );
}

// ── 패널 ──────────────────────────────────────────────────────────────────

export interface LayoutPropertyPanelProps {
  obj: LayoutObject;
  cellRange: CellRange | null;
  roles: SignerRole[];
  onChange: (next: LayoutObject) => void;
  onDelete: () => void;
  onCellRange: (r: CellRange | null) => void;
  /** 칸에 넣을 사진을 고른다(라이브러리). 취소면 null. */
  onPickImage: () => Promise<LayoutImageRef | null>;
}

export function LayoutPropertyPanel({
  obj,
  cellRange,
  roles,
  onChange,
  onDelete,
  onCellRange,
  onPickImage,
}: LayoutPropertyPanelProps) {
  const isLine = obj.kind === 'shape' && obj.shape === 'line';
  const setPos = (patch: Partial<Pick<LayoutObject, 'x' | 'y'>>) => {
    if (obj.kind === 'shape' && obj.line) {
      const dx = (patch.x ?? obj.x) - obj.x;
      const dy = (patch.y ?? obj.y) - obj.y;
      onChange(
        layoutLineWithEnds(obj, {
          x1: obj.line.x1 + dx,
          y1: obj.line.y1 + dy,
          x2: obj.line.x2 + dx,
          y2: obj.line.y2 + dy,
        }),
      );
      return;
    }
    onChange({ ...obj, ...patch } as LayoutObject);
  };
  const setSize = (w: number, h: number) => {
    if (obj.kind === 'table') onChange(layoutResizeTable(obj, w, h));
    else onChange({ ...obj, w, h } as LayoutObject);
  };
  const alignOnPage = (where: 'left' | 'center' | 'right') => {
    const x =
      where === 'left'
        ? 0
        : where === 'center'
          ? Math.round((LAYOUT_PAGE_W - obj.w) / 2)
          : LAYOUT_PAGE_W - obj.w;
    setPos({ x: Math.max(0, x) });
  };

  return (
    <div style={{ padding: 16, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <div style={{ fontSize: 15, fontWeight: 700 }}>
        {obj.kind === 'table'
          ? '표 속성'
          : obj.kind === 'text'
            ? '텍스트 속성'
            : obj.kind === 'shape'
              ? '도형 속성'
              : '사진 속성'}
      </div>

      {obj.kind === 'table' && (
        <TableTools
          t={obj}
          cellRange={cellRange}
          roles={roles}
          onChange={onChange}
          onCellRange={onCellRange}
          onPickImage={onPickImage}
        />
      )}

      {obj.kind === 'text' && (
        <>
          <div style={sectionTitle}>글자</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <Num
              label="크기(px)"
              value={obj.fontSize}
              min={6}
              max={120}
              onCommit={(v) => onChange({ ...obj, fontSize: v })}
            />
            <Row label="색">
              <ColorInput
                value={obj.color ?? '#222222'}
                onChange={(v) => onChange({ ...obj, color: v })}
              />
            </Row>
          </div>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginTop: 8 }}
          >
            <input
              type="checkbox"
              checked={!!obj.bold}
              onChange={(e) => onChange({ ...obj, bold: e.target.checked })}
            />
            굵게
          </label>
          <div style={sectionTitle}>텍스트 정렬</div>
          <Seg<LayoutTextAlign>
            value={obj.align ?? DEFAULT_LAYOUT_TEXT_ALIGN}
            options={[
              { v: 'left', label: '좌' },
              { v: 'center', label: '중앙' },
              { v: 'right', label: '우' },
            ]}
            onChange={(v) => onChange({ ...obj, align: v })}
          />
        </>
      )}

      {obj.kind === 'shape' && (
        <>
          <div style={sectionTitle}>도형</div>
          <Seg<'rect' | 'ellipse' | 'line'>
            value={obj.shape}
            options={[
              { v: 'rect', label: '사각형' },
              { v: 'ellipse', label: '원' },
              { v: 'line', label: '선' },
            ]}
            onChange={(v) => {
              if (v === 'line') {
                // 선으로 바꾸면 상자 가운데를 지나는 **가로 직선**이 된다. 끝점은 캔버스에서 끈다.
                const yMid = obj.y + Math.round(obj.h / 2);
                onChange(
                  layoutLineWithEnds(
                    { ...obj, shape: 'line' },
                    { x1: obj.x, y1: yMid, x2: obj.x + obj.w, y2: yMid },
                  ),
                );
                return;
              }
              const { line: _drop, ...rest } = obj;
              onChange({ ...rest, shape: v, h: Math.max(rest.h, 40) });
            }}
          />
          <div style={sectionTitle}>{isLine ? '선' : '테두리'}</div>
          <BorderEditor value={obj.stroke} onChange={(stroke) => onChange({ ...obj, stroke })} />
          {isLine && (
            <p
              style={{
                margin: '8px 0 0',
                fontSize: 11.5,
                color: 'var(--text-secondary)',
                lineHeight: 1.6,
              }}
            >
              양 끝의 동그란 손잡이를 끌어 방향·길이를 바꿉니다. Shift 를 누르면 45° 단위로
              붙습니다.
            </p>
          )}
          {!isLine && (
            <>
              <div style={sectionTitle}>채움</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
                <Row label="색">
                  <ColorInput
                    value={obj.fill ?? '#ffffff'}
                    onChange={(v) => onChange({ ...obj, fill: v })}
                  />
                </Row>
                <button
                  type="button"
                  style={smallBtn}
                  onClick={() => onChange({ ...obj, fill: null })}
                >
                  채움 없음
                </button>
              </div>
            </>
          )}
        </>
      )}

      {obj.kind === 'image' && (
        <p
          style={{
            margin: '10px 0 0',
            fontSize: 12,
            color: 'var(--text-secondary)',
            lineHeight: 1.6,
          }}
        >
          {obj.fileName}
          <br />
          모서리를 끌어 크기를 자유롭게 바꿉니다. 그릴 때는 비율을 지켜 상자 안에 맞춥니다.
        </p>
      )}

      <div style={sectionTitle}>위치 / 크기 (px)</div>
      <div style={{ display: 'flex', gap: 6 }}>
        <Num
          label="X"
          value={obj.x}
          min={0}
          max={LAYOUT_PAGE_W - obj.w}
          onCommit={(v) => setPos({ x: v })}
          testId="prop-x"
        />
        <Num
          label="Y (위에서부터)"
          value={obj.y}
          min={0}
          onCommit={(v) => setPos({ y: v })}
          testId="prop-y"
        />
      </div>
      {!isLine && (
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <Num
            label="너비"
            value={obj.w}
            min={12}
            max={LAYOUT_PAGE_W - obj.x}
            onCommit={(v) => setSize(v, obj.h)}
            testId="prop-w"
          />
          <Num
            label="높이"
            value={obj.h}
            min={1}
            onCommit={(v) => setSize(obj.w, v)}
            testId="prop-h"
          />
        </div>
      )}

      <div style={sectionTitle}>정렬</div>
      <Seg<'left' | 'center' | 'right'>
        value={obj.x === 0 ? 'left' : obj.x + obj.w >= LAYOUT_PAGE_W ? 'right' : 'center'}
        options={[
          { v: 'left', label: '왼쪽' },
          { v: 'center', label: '가운데' },
          { v: 'right', label: '오른쪽' },
        ]}
        onChange={alignOnPage}
      />

      <button
        type="button"
        onClick={onDelete}
        data-testid="delete-object"
        style={{
          marginTop: 18,
          padding: '9px 0',
          border: 'none',
          borderRadius: 'var(--r-btn)',
          background: 'var(--fg-danger)',
          color: 'var(--bg-surface)',
          fontWeight: 700,
          fontSize: 13,
          cursor: 'pointer',
        }}
      >
        {obj.kind === 'table' ? '표 삭제' : '객체 삭제'}
      </button>
    </div>
  );
}

// ── 표 도구 ───────────────────────────────────────────────────────────────

// ── 엑셀식 테두리 아이콘 — 2×2 격자에서 적용될 선만 진하게 ─────────────────
function EdgeIcon({ lit }: { lit: ('t' | 'b' | 'l' | 'r' | 'h' | 'v')[] }) {
  const on = (k: 't' | 'b' | 'l' | 'r' | 'h' | 'v') => lit.includes(k);
  const dim = 'color-mix(in srgb, currentColor 28%, transparent)';
  const L = (
    k: 't' | 'b' | 'l' | 'r' | 'h' | 'v',
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ) => (
    <line
      x1={x1}
      y1={y1}
      x2={x2}
      y2={y2}
      stroke={on(k) ? 'currentColor' : dim}
      strokeWidth={on(k) ? 2 : 1}
      strokeDasharray={on(k) ? undefined : '1.5 1.5'}
    />
  );
  return (
    <svg width={20} height={20} viewBox="0 0 20 20" aria-hidden>
      {L('t', 2, 2, 18, 2)}
      {L('b', 2, 18, 18, 18)}
      {L('l', 2, 2, 2, 18)}
      {L('r', 18, 2, 18, 18)}
      {L('h', 2, 10, 18, 10)}
      {L('v', 10, 2, 10, 18)}
    </svg>
  );
}

const BORDER_MODES: {
  mode: LayoutBorderApply;
  label: string;
  lit: ('t' | 'b' | 'l' | 'r' | 'h' | 'v')[];
}[] = [
  { mode: 'all', label: '모든 테두리', lit: ['t', 'b', 'l', 'r', 'h', 'v'] },
  { mode: 'outer', label: '바깥쪽', lit: ['t', 'b', 'l', 'r'] },
  { mode: 'inner', label: '안쪽', lit: ['h', 'v'] },
  { mode: 'top', label: '위', lit: ['t'] },
  { mode: 'bottom', label: '아래', lit: ['b'] },
  { mode: 'left', label: '왼쪽', lit: ['l'] },
  { mode: 'right', label: '오른쪽', lit: ['r'] },
  { mode: 'none', label: '테두리 없음', lit: [] },
];

function TableTools({
  t,
  cellRange,
  roles,
  onChange,
  onCellRange,
  onPickImage,
}: {
  t: LayoutTable;
  cellRange: CellRange | null;
  roles: SignerRole[];
  onChange: (next: LayoutTable) => void;
  onCellRange: (r: CellRange | null) => void;
  onPickImage: () => Promise<LayoutImageRef | null>;
}) {
  const anchor = cellRange ? layoutMasterOf(t, cellRange.a.r, cellRange.a.c) : null;
  const anchorCell = anchor ? t.cells[anchor.r]?.[anchor.c] : undefined;
  const isRange =
    !!cellRange && (cellRange.a.r !== cellRange.b.r || cellRange.a.c !== cellRange.b.c);
  const canSplit = !!anchorCell && ((anchorCell.rowSpan ?? 1) > 1 || (anchorCell.colSpan ?? 1) > 1);
  const rows = t.rowHeights.length;
  const cols = t.colWidths.length;
  const selectedCount = cellRange ? mastersInRange(t, cellRange).length : 0;
  // 고른 범위(병합까지 넓힌) — 열 너비·행 높이·테두리가 이 범위에 먹는다.
  const span = cellRange ? layoutExpandRange(t, cellRange.a, cellRange.b) : null;

  // 테두리 펜 — 엑셀처럼 두께·모양·색을 먼저 고르고, 어느 변에 칠지 누른다.
  // 표에 저장한다(2026-09-23 오너 요청): 다른 표를 골랐다 와도, 저장했다 열어도 그대로이고,
  // 엑셀로 가져온 표는 그 표에 그어진 가장 흔한 선으로 시작한다(예전엔 늘 검정 1pt).
  const pen = layoutTablePen(t);
  const setPen = (next: LayoutBorder) =>
    onChange({ ...t, pen: { width: next.width, color: next.color, style: next.style } });

  /** 범위 안 주인 칸 모두에 패치. */
  const patchCells = (
    patch: Partial<LayoutTableCell> | ((c: LayoutTableCell) => LayoutTableCell),
  ) => {
    if (!cellRange) return;
    const targets = mastersInRange(t, cellRange);
    onChange({
      ...t,
      cells: t.cells.map((row, r) =>
        row.map((cell, c) =>
          targets.some((p) => p.r === r && p.c === c)
            ? typeof patch === 'function'
              ? patch(cell)
              : { ...cell, ...patch }
            : cell,
        ),
      ),
    });
  };

  const setColWidths = (w: number) => {
    if (!span) return;
    let next = t;
    for (let c = span.c1; c <= span.c2; c++) next = layoutSetColWidth(next, c, w);
    // 종이 폭을 넘으면 넘친 만큼 비율로 줄인다 — 표가 종이 밖으로 나가면 저장이 막힌다.
    if (next.x + next.w > LAYOUT_PAGE_W)
      next = layoutResizeTable(next, LAYOUT_PAGE_W - next.x, next.h);
    onChange(next);
  };
  const setRowHeights = (h: number) => {
    if (!span) return;
    let next = t;
    for (let r = span.r1; r <= span.r2; r++) next = layoutSetRowHeight(next, r, h);
    onChange(next);
  };

  const field = anchorCell?.field;
  const image = anchorCell?.image ?? null;
  const defaultSlot = roles[0]?.slot ?? 'customer';

  return (
    <>
      <div style={sectionTitle}>행과 열</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        <Tool
          icon={icons.rowAbove}
          label={'위에\n행 삽입'}
          disabled={!anchor}
          testId="row-above"
          onClick={() => anchor && onChange(layoutInsertRow(t, anchor.r))}
        />
        <Tool
          icon={icons.rowBelow}
          label={'아래\n행 삽입'}
          disabled={!anchor}
          testId="row-below"
          onClick={() =>
            anchor && onChange(layoutInsertRow(t, anchor.r + (anchorCell?.rowSpan ?? 1)))
          }
        />
        <Tool
          icon={icons.colLeft}
          label={'왼쪽\n열 삽입'}
          disabled={!anchor}
          testId="col-left"
          onClick={() => anchor && onChange(layoutInsertCol(t, anchor.c))}
        />
        <Tool
          icon={icons.colRight}
          label={'오른쪽에\n열을 삽입'}
          disabled={!anchor}
          testId="col-right"
          onClick={() =>
            anchor && onChange(layoutInsertCol(t, anchor.c + (anchorCell?.colSpan ?? 1)))
          }
        />
        <Tool
          icon={icons.merge}
          label={'셀 병합'}
          disabled={!isRange}
          testId="merge-cells"
          onClick={() => {
            if (!cellRange) return;
            onChange(layoutMergeCells(t, cellRange.a, cellRange.b));
            const m = layoutMasterOf(
              t,
              Math.min(cellRange.a.r, cellRange.b.r),
              Math.min(cellRange.a.c, cellRange.b.c),
            );
            onCellRange({ a: m, b: m });
          }}
        />
        <Tool
          icon={icons.split}
          label={'셀 분할'}
          disabled={!canSplit}
          testId="split-cell"
          onClick={() => anchor && onChange(layoutSplitCell(t, anchor.r, anchor.c))}
        />
        <Tool
          icon={icons.rowDelete}
          label={'행 삭제'}
          disabled={!anchor || rows <= 1}
          testId="delete-row"
          onClick={() => {
            if (!anchor) return;
            onChange(layoutDeleteRow(t, anchor.r));
            onCellRange(null);
          }}
        />
        <Tool
          icon={icons.colDelete}
          label={'열 삭제'}
          disabled={!anchor || cols <= 1}
          testId="delete-col"
          onClick={() => {
            if (!anchor) return;
            onChange(layoutDeleteCol(t, anchor.c));
            onCellRange(null);
          }}
        />
      </div>

      {anchor && span ? (
        <>
          {/* 칸 종류 — 행·열 바로 밑(2026-10-20 오너). */}
          <div style={sectionTitle}>칸 종류</div>
          <Row label="이 칸은">
            <select
              style={selectSt}
              aria-label="칸 종류"
              value={field?.type ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) {
                  patchCells((c) => {
                    const { field: _drop, ...rest } = c;
                    return rest;
                  });
                  return;
                }
                const type = v as FieldType;
                patchCells((c) => ({
                  ...c,
                  // 2026-10-20 오너 — 글자 칸을 입력 칸으로 바꾸면 가운데 정렬로(엑셀에서 온 칸은
                  // 왼쪽 정렬이 박혀 있어 매번 다시 가운데로 바꿔야 했다). 이미 입력 칸이면 그대로.
                  ...(c.field ? {} : { align: 'center' as const }),
                  field: {
                    slot: defaultSlot,
                    ...c.field,
                    type,
                    ...(type === 'dropdown' && !c.field?.options
                      ? { options: ['예', '아니오'] }
                      : {}),
                  },
                }));
              }}
            >
              <option value="">글자 칸</option>
              {LAYOUT_CELL_FIELD_TYPES.map((ft) => (
                <option key={ft} value={ft}>
                  {FIELD_TYPE_LABELS[ft]} 입력 칸
                </option>
              ))}
            </select>
          </Row>

          {field && (
            <>
              <div style={{ height: 6 }} />
              <Row label="자동 채움">
                <select
                  style={selectSt}
                  aria-label="자동 채움"
                  value={bindingKey(field.binding)}
                  onChange={(e) => {
                    const opt = BINDING_OPTIONS.find((o) => o.value === e.target.value);
                    const binding = opt?.binding ?? { kind: 'literal' as const };
                    const bound = binding.kind !== 'literal';
                    patchCells((c) =>
                      c.field
                        ? { ...c, field: { ...c.field, binding, slot: bound ? null : defaultSlot } }
                        : c,
                    );
                  }}
                >
                  {BINDING_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Row>
              {bindingKey(field.binding) === 'literal' && (
                <>
                  <div style={{ height: 6 }} />
                  <Row label="채울 사람">
                    <select
                      style={selectSt}
                      aria-label="채울 사람"
                      value={field.slot ?? ''}
                      onChange={(e) =>
                        patchCells((c) =>
                          c.field
                            ? { ...c, field: { ...c.field, slot: e.target.value || null } }
                            : c,
                        )
                      }
                    >
                      {roles.length === 0 && <option value="customer">고객(기본)</option>}
                      {roles.map((r) => (
                        <option key={r.slot} value={r.slot}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  </Row>
                </>
              )}
              {field.type === 'dropdown' && (
                <>
                  <div style={{ height: 6 }} />
                  <Row label="선택지 (쉼표로 구분)">
                    <input
                      style={inputSt}
                      aria-label="드롭다운 선택지"
                      value={(field.options ?? []).join(', ')}
                      onChange={(e) =>
                        patchCells((c) =>
                          c.field
                            ? {
                                ...c,
                                field: {
                                  ...c.field,
                                  options: e.target.value
                                    .split(',')
                                    .map((x) => x.trim())
                                    .filter(Boolean),
                                },
                              }
                            : c,
                        )
                      }
                    />
                  </Row>
                </>
              )}
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  marginTop: 8,
                }}
              >
                <input
                  type="checkbox"
                  checked={!!field.required}
                  onChange={(e) =>
                    patchCells((c) =>
                      c.field ? { ...c, field: { ...c.field, required: e.target.checked } } : c,
                    )
                  }
                />
                필수
              </label>
            </>
          )}

          <div style={sectionTitle}>
            {isRange
              ? `고른 칸 ${selectedCount}개`
              : `${String.fromCharCode(65 + Math.min(25, anchor.c))}${anchor.r + 1} 칸`}
          </div>

          {/* 열 너비·행 높이(숫자) — 고른 범위의 열·행 모두에 (오너 요청 2026-09-23) */}
          <div style={{ display: 'flex', gap: 6 }}>
            <Num
              label={
                span.c1 === span.c2 ? '열 너비(px)' : `열 너비(px) · ${span.c2 - span.c1 + 1}열`
              }
              value={t.colWidths[span.c1]}
              min={MIN_LAYOUT_CELL}
              max={LAYOUT_PAGE_W}
              testId="col-width"
              onCommit={setColWidths}
            />
            <Num
              label={
                span.r1 === span.r2 ? '행 높이(px)' : `행 높이(px) · ${span.r2 - span.r1 + 1}행`
              }
              value={t.rowHeights[span.r1]}
              min={MIN_LAYOUT_CELL}
              max={1122}
              testId="row-height"
              onCommit={setRowHeights}
            />
          </div>

          <div style={sectionTitle}>글자</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            <Num
              label="크기(px)"
              value={anchorCell?.fontSize ?? t.fontSize}
              min={6}
              max={72}
              testId="cell-font-size"
              onCommit={(v) => patchCells({ fontSize: v })}
            />
            <Row label="색">
              <ColorInput
                testId="cell-text-color"
                value={anchorCell?.color ?? '#222222'}
                onChange={(v) => patchCells({ color: v })}
              />
            </Row>
          </div>
          <label
            style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, marginTop: 6 }}
          >
            <input
              type="checkbox"
              checked={!!anchorCell?.bold}
              onChange={(e) => patchCells({ bold: e.target.checked })}
            />
            굵게
          </label>

          {/* 엑셀식 테두리 — 펜을 고르고, 칠할 변을 누른다 */}
          <div style={sectionTitle}>테두리</div>
          <BorderEditor value={pen} testId="border-pen-width" onChange={setPen} />
          <div
            style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 4, marginTop: 8 }}
          >
            {BORDER_MODES.map((b) => (
              <button
                key={b.mode}
                type="button"
                data-testid={`border-${b.mode}`}
                title={b.label}
                aria-label={b.label}
                onClick={() =>
                  onChange(layoutApplyBorders(t, cellRange!.a, cellRange!.b, b.mode, pen))
                }
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                  padding: '4px 0',
                  border: '1px solid var(--border-default)',
                  borderRadius: 'var(--r-btn)',
                  background: 'var(--bg-surface)',
                  color: 'var(--fg-default)',
                  cursor: 'pointer',
                  fontSize: 11,
                }}
              >
                <EdgeIcon lit={b.lit} />
                {b.label}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="border-reset"
            style={{ ...smallBtn, marginTop: 6, alignSelf: 'flex-start' }}
            onClick={() =>
              onChange(layoutApplyBorders(t, cellRange!.a, cellRange!.b, 'reset', pen))
            }
          >
            고른 칸 테두리를 표 기본으로
          </button>

          {/* 정렬 — 테두리 바로 밑(2026-10-20 오너). */}
          <div style={sectionTitle}>{image ? '글자와 사진 정렬' : '텍스트 정렬'}</div>
          <Seg<LayoutTextAlign>
            value={anchorCell?.align ?? DEFAULT_LAYOUT_TEXT_ALIGN}
            options={[
              { v: 'left', label: '좌' },
              { v: 'center', label: '중앙' },
              { v: 'right', label: '우' },
            ]}
            onChange={(v) => patchCells({ align: v })}
          />
          <div style={{ height: 6 }} />
          <Seg<'top' | 'middle' | 'bottom'>
            value={anchorCell?.valign ?? 'middle'}
            options={[
              { v: 'top', label: '위' },
              { v: 'middle', label: '가운데' },
              { v: 'bottom', label: '아래' },
            ]}
            onChange={(v) => patchCells({ valign: v })}
          />

          <div style={sectionTitle}>음영</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
            <Row label="색">
              <ColorInput
                testId="cell-fill"
                value={anchorCell?.fill ?? '#ffffff'}
                onChange={(v) => patchCells({ fill: v })}
              />
            </Row>
            <button type="button" style={smallBtn} onClick={() => patchCells({ fill: null })}>
              음영 없음
            </button>
          </div>

          {/* 칸 사진 — 맞춤 방식·여백·불투명도(오너 요청 2026-09-23 "조금 더 구체화") */}
          <div style={sectionTitle}>사진</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button
              type="button"
              style={photoBtn}
              data-testid="cell-image"
              onClick={async () => {
                const picked = await onPickImage();
                if (picked) patchCells((c) => ({ ...c, image: { ...(c.image ?? {}), ...picked } }));
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.4"
                aria-hidden
              >
                <rect x="1.5" y="2.5" width="13" height="11" rx="1.5" />
                <circle cx="5.5" cy="6" r="1.3" />
                <path d="M2 12.5l4-4 3 3 2-2 3.5 3" />
              </svg>
              {image ? '사진 바꾸기' : '칸에 사진 넣기'}
            </button>
            {image && (
              <button type="button" style={smallBtn} onClick={() => patchCells({ image: null })}>
                사진 지우기
              </button>
            )}
          </div>
          {image && (
            <>
              <div style={{ height: 8 }} />
              <Row label="맞춤">
                <Seg<LayoutImageFit>
                  value={image.fit ?? 'contain'}
                  options={[
                    { v: 'contain', label: '비율 맞춤' },
                    { v: 'cover', label: '꽉 채우기' },
                    { v: 'stretch', label: '늘이기' },
                  ]}
                  onChange={(v) =>
                    patchCells((c) => (c.image ? { ...c, image: { ...c.image, fit: v } } : c))
                  }
                />
              </Row>
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                <Num
                  label="여백(px)"
                  value={image.padding ?? DEFAULT_CELL_IMAGE_PADDING}
                  min={0}
                  max={40}
                  testId="cell-image-padding"
                  onCommit={(v) =>
                    patchCells((c) => (c.image ? { ...c, image: { ...c.image, padding: v } } : c))
                  }
                />
                <Num
                  label="불투명도(%)"
                  value={Math.round((image.opacity ?? 1) * 100)}
                  min={5}
                  max={100}
                  testId="cell-image-opacity"
                  onCommit={(v) =>
                    patchCells((c) =>
                      c.image ? { ...c, image: { ...c.image, opacity: v / 100 } } : c,
                    )
                  }
                />
              </div>
              <p
                style={{
                  margin: '6px 0 0',
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                  lineHeight: 1.55,
                }}
              >
                사진 위치는 위의 정렬 설정을 따릅니다. 음영은 사진 밑에 깔려서, 투명 PNG 나
                불투명도를 낮춘 사진 사이로 비칩니다.
              </p>
            </>
          )}
        </>
      ) : (
        <p
          style={{
            margin: '10px 0 0',
            fontSize: 12.5,
            color: 'var(--text-secondary)',
            lineHeight: 1.6,
          }}
        >
          칸을 누르면 글자를 칠 수 있고, 누른 채 끌거나 Shift+클릭으로 여러 칸을 고릅니다. 고른 칸의
          열 너비, 행 높이, 글자, 테두리, 음영, 사진, 칸 종류를 여기서 정합니다. 테두리는 엑셀처럼
          변마다 따로 정할 수 있습니다.
        </p>
      )}
    </>
  );
}
